import axios, { AxiosRequestHeaders, AxiosRequestConfig } from "axios";
import * as requestInterceptors from "./interceptors/request-interceptors";
import responseInterceptors from "./interceptors/response-interceptors";
import errorInterceptors from "./interceptors/error-interceptors";
import { Methods, RequestResponse } from "./types";
const isProduction = process.env.NODE_ENV === "production";
const timeout = isProduction ? 90 * 1000 : 60 * 1000;
const instance = axios.create({ timeout, withCredentials: true });

type IrequestInterceptors = keyof typeof requestInterceptors;

Reflect.ownKeys(requestInterceptors).forEach((key) => {
  instance.interceptors.request.use(
    requestInterceptors[key as IrequestInterceptors]
  );
});
instance.interceptors.response.use(responseInterceptors, errorInterceptors);

export interface RequestOptions {
  method?: Methods;
  headers?: AxiosRequestHeaders;
  returnDataOnly?: boolean;
  isUserQuery?: boolean;
  config?: AxiosRequestConfig;
}

async function baseRequest<T = any>(
  method: Methods,
  url: string,
  data?: any,
  options: RequestOptions = {}
): Promise<RequestResponse<T> | never> {
  const { returnDataOnly = true, headers = {}, config = {} } = options;
  const res = await instance({
    method,
    url,
    headers,
    ...(method === Methods.GET ? { params: data } : { data }),
    ...config,
  });

  const { isUserQuery } = options;
  if (isUserQuery) {
    return returnDataOnly ? res.data.data : res.data;
  }
  return returnDataOnly ? res.data : res;
}

export type RequestFunc = {
  <T = any>(url: string, data?: any, options?: RequestOptions): Promise<
    RequestResponse<T> | never
  >;
};

function buildRequestFunctionFromMethod(method: Methods) {
  return baseRequest.bind(undefined, method) as RequestFunc;
}

const request = {
  get: buildRequestFunctionFromMethod(Methods.GET),
  post: buildRequestFunctionFromMethod(Methods.POST),
  put: buildRequestFunctionFromMethod(Methods.PUT),
  delete: buildRequestFunctionFromMethod(Methods.DELETE),
  patch: buildRequestFunctionFromMethod(Methods.PATCH),
};

export default request;
